import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
    getCollections,
    getCollectionMessages,
    searchCollectionMessages,
} from "../services/mongoServices";
import CollectionsList from "../components/messages/CollectionsList";
import MessageList from "../components/messages/MessageList";
import JsonPanel from "../components/messages/JsonPanel";

const getTimestampValue = (message) => {
    const value = message?.timestamp ?? message?.time;
    if (value === undefined || value === null) return 0;

    const numericValue = Number(value);
    if (!Number.isNaN(numericValue)) return numericValue;

    const dateValue = new Date(value).getTime();
    return Number.isNaN(dateValue) ? 0 : dateValue;
};

const sortMessagesByTimestampDesc = (documents = []) =>
    [...documents].sort(
        (a, b) => getTimestampValue(b) - getTimestampValue(a),
    );

const MessagesPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [collections, setCollections] = useState([]);
    const [filteredCollections, setFilteredCollections] = useState([]);
    const [selectedCollectionName, setSelectedCollectionName] = useState(null);
    const [messages, setMessages] = useState([]);
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [loadingCollections, setLoadingCollections] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [loadingMore, setLoadingMore] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const displayMode = searchParams.get("display") === "1";

    // Read initial values from URL (also when coming back from another page)
    useEffect(() => {
        const collectionParam = searchParams.get("collection");
        const searchParam = searchParams.get("search");
        
        if (searchParam) {
            setSearchQuery(searchParam);
            setIsSearching(true);
        } else {
            setSearchQuery("");
            setIsSearching(false);
        }
        
        if (collectionParam && collectionParam !== selectedCollectionName) {
            setSelectedCollectionName(collectionParam);
        }
    }, [searchParams]); // Listen to searchParams changes, not just on mount

    // Add keyframe animation for spinner
    useEffect(() => {
        const style = document.createElement("style");
        style.textContent = `
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
        document.head.appendChild(style);
        return () => document.head.removeChild(style);
    }, []);

    // Fetch collections on mount
    useEffect(() => {
        const fetchCollections = async () => {
            try {
                setLoadingCollections(true);
                const collections = await getCollections();
                setCollections(collections);
            } catch (error) {
                console.error("Error fetching collections:", error);
                setCollections([]);
            } finally {
                setLoadingCollections(false);
            }
        };

        fetchCollections();
    }, []);

    // Filter collections based on URL parameter 'uc'
    useEffect(() => {
        const ucParam = searchParams.get("uc");
        const collectionParam = searchParams.get("collection");
        
        // Save the current state: are we using uc filter or not?
        if (ucParam && ucParam.trim() !== "") {
            sessionStorage.setItem("lastUcFilter", ucParam);
            sessionStorage.setItem("usingUcFilter", "true");
        } else if (ucParam === null && !sessionStorage.getItem("restoringFromDevices")) {
            // If explicitly navigating to /messages without uc, clear the uc filter mode
            sessionStorage.removeItem("lastUcFilter");
            sessionStorage.setItem("usingUcFilter", "false");
        }
        
        // Clear the restoration flag
        sessionStorage.removeItem("restoringFromDevices");
        
        if (!ucParam || ucParam.trim() === "") {
            setFilteredCollections(collections);
        } else {
            const filtered = collections.filter((collection) =>
                collection.toLowerCase().includes(ucParam.toLowerCase())
            );
            setFilteredCollections(filtered);
        }

        // Only auto-select if there's no collection in URL AND no collection selected
        if (collections.length > 0 && !collectionParam && !selectedCollectionName) {
            const collectionsToUse = ucParam && ucParam.trim() !== "" 
                ? collections.filter((collection) =>
                    collection.toLowerCase().includes(ucParam.toLowerCase())
                  )
                : collections;
            
            if (collectionsToUse.length > 0) {
                const firstCollection = collectionsToUse[0];
                setSelectedCollectionName(firstCollection);
                // Update URL with selected collection
                const newParams = new URLSearchParams(searchParams);
                newParams.set("collection", firstCollection);
                setSearchParams(newParams, { replace: true });
                // Save to sessionStorage for persistence
                sessionStorage.setItem("lastSelectedCollection", firstCollection);
            } else {
                setSelectedCollectionName(null);
            }
        }
    }, [collections, searchParams]);

    // Fetch messages when collection or search changes
    useEffect(() => {
        if (!selectedCollectionName) {
            setMessages([]);
            setSelectedMessage(null);
            setCurrentPage(1);
            setTotalPages(1);
            setTotalCount(0);
            return;
        }

        const fetchMessages = async () => {
            try {
                setLoadingMessages(true);
                
                // Check if we need to search or just load messages
                const searchParam = searchParams.get("search");
                
                if (searchParam) {
                    // Perform search
                    const data = await searchCollectionMessages(
                        selectedCollectionName,
                        searchParam,
                        1,
                    );
                    const sortedDocuments = sortMessagesByTimestampDesc(
                        data.documents,
                    );
                    setMessages(sortedDocuments);
                    setCurrentPage(data.page);
                    setTotalPages(data.totalPages);
                    setTotalCount(data.totalCount);
                    setSearchQuery(searchParam);
                    setIsSearching(true);
                    // Automatically select the first message
                    if (sortedDocuments.length > 0) {
                        setSelectedMessage(sortedDocuments[0]);
                    } else {
                        setSelectedMessage(null);
                    }
                } else {
                    // Load normal messages
                    const data = await getCollectionMessages(
                        selectedCollectionName,
                        1,
                    );
                    const sortedDocuments = sortMessagesByTimestampDesc(
                        data.documents,
                    );
                    setMessages(sortedDocuments);
                    setCurrentPage(data.page);
                    setTotalPages(data.totalPages);
                    setTotalCount(data.totalCount);
                    setSearchQuery("");
                    setIsSearching(false);
                    // Automatically select the first message
                    if (sortedDocuments.length > 0) {
                        setSelectedMessage(sortedDocuments[0]);
                    } else {
                        setSelectedMessage(null);
                    }
                }
            } catch (error) {
                console.error("Error fetching messages:", error);
                setMessages([]);
                setSelectedMessage(null);
                setCurrentPage(1);
                setTotalPages(1);
                setTotalCount(0);
            } finally {
                setLoadingMessages(false);
            }
        };

        fetchMessages();
    }, [selectedCollectionName, searchParams]);

    // Function to handle search
    const handleSearch = async (query) => {
        if (!selectedCollectionName) return;

        const newParams = new URLSearchParams(searchParams);
        
        if (query && query.trim()) {
            // Add search to URL
            newParams.set("search", query);
            setIsSearching(true);
        } else {
            // Remove search from URL
            newParams.delete("search");
            setIsSearching(false);
        }
        
        setSearchParams(newParams);
        setSearchQuery(query);
    };

    // Function to load more messages (next page)
    const handleLoadMore = async () => {
        if (currentPage >= totalPages || loadingMore) return;

        try {
            setLoadingMore(true);
            const nextPage = currentPage + 1;
            let data;

            if (isSearching && searchQuery) {
                data = await searchCollectionMessages(
                    selectedCollectionName,
                    searchQuery,
                    nextPage,
                );
            } else {
                data = await getCollectionMessages(
                    selectedCollectionName,
                    nextPage,
                );
            }

            setMessages((prevMessages) =>
                sortMessagesByTimestampDesc([
                    ...prevMessages,
                    ...data.documents,
                ]),
            );
            setCurrentPage(data.page);
        } catch (error) {
            console.error("Error loading more messages:", error);
        } finally {
            setLoadingMore(false);
        }
    };

    const handleCollectionSelect = (name) => {
        setSelectedCollectionName(name);
        setSelectedMessage(null);
        
        // Update URL with new collection
        const newParams = new URLSearchParams(searchParams);
        newParams.set("collection", name);
        setSearchParams(newParams);
        
        // Save to sessionStorage for persistence
        sessionStorage.setItem("lastSelectedCollection", name);
    };

    const handleMessageSelect = (msg) => setSelectedMessage(msg);

    return (
        <div
            className={displayMode ? "messages-display-mode" : ""}
            style={{ display: "flex", flex: 1, height: "100%", minWidth: 0 }}
        >
            <CollectionsList
                collections={filteredCollections}
                selectedName={selectedCollectionName}
                onSelect={handleCollectionSelect}
                loading={loadingCollections}
                displayMode={displayMode}
            />
            <MessageList
                messages={messages}
                onSelect={handleMessageSelect}
                selectedMessageId={selectedMessage?._id}
                loading={loadingMessages}
                currentPage={currentPage}
                totalPages={totalPages}
                totalCount={totalCount}
                onLoadMore={handleLoadMore}
                loadingMore={loadingMore}
                onSearch={handleSearch}
                searchQuery={searchQuery}
                isSearching={isSearching}
                displayMode={displayMode}
            />
            <JsonPanel data={selectedMessage} displayMode={displayMode} />
        </div>
    );
};

export default MessagesPage;
